/**
 * useCountdown
 *
 * A precise, drift-corrected countdown timer.
 *
 * @param {number|null} totalSeconds  — time limit in seconds, null = unlimited
 * @param {Date|string} startedAt     — ISO string or Date when the exam started
 * @param {function}    onExpire      — called exactly once when the timer hits 0
 *
 * Returns
 * ───────
 * {
 *   remaining  : number   — seconds left (integer ≥ 0)
 *   formatted  : string   — "MM:SS" or "H:MM:SS"
 *   isWarning  : boolean  — true when ≤ 60 s left
 *   isDanger   : boolean  — true when ≤ 30 s left
 *   isExpired  : boolean  — true when remaining === 0
 * }
 */

import { useCallback, useEffect, useRef, useState } from 'react'

function computeRemaining(totalSeconds, startedAt) {
  if (totalSeconds == null) return Infinity
  const elapsed = (Date.now() - new Date(startedAt).getTime()) / 1000
  return Math.max(0, Math.floor(totalSeconds - elapsed))
}

function formatTime(seconds) {
  if (!isFinite(seconds)) return '∞'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

export function useCountdown(totalSeconds, startedAt, onExpire) {
  const [remaining, setRemaining] = useState(() =>
    computeRemaining(totalSeconds, startedAt),
  )
  const onExpireRef  = useRef(onExpire)
  const expiredFired = useRef(false)

  // Keep callback ref fresh without restarting the timer
  useEffect(() => { onExpireRef.current = onExpire }, [onExpire])

  useEffect(() => {
    // No limit or missing start time → nothing to count down
    if (totalSeconds == null || !startedAt) return

    expiredFired.current = false

    // Drift-corrected tick: schedule next tick relative to the next whole
    // second boundary so we never drift by accumulating setTimeout delays.
    let timeoutId

    function tick() {
      const rem = computeRemaining(totalSeconds, startedAt)
      setRemaining(rem)

      if (rem <= 0) {
        if (!expiredFired.current) {
          expiredFired.current = true
          onExpireRef.current?.()
        }
        return // Stop scheduling
      }

      // Align to the next whole second
      const msUntilNextSecond =
        1000 - ((Date.now() - new Date(startedAt).getTime()) % 1000)
      timeoutId = setTimeout(tick, msUntilNextSecond)
    }

    tick()
    return () => clearTimeout(timeoutId)
  }, [totalSeconds, startedAt])

  return {
    remaining: isFinite(remaining) ? remaining : null,
    formatted:  formatTime(remaining),
    isWarning:  isFinite(remaining) && remaining <= 60,
    isDanger:   isFinite(remaining) && remaining <= 30,
    isExpired:  isFinite(remaining) && remaining <= 0,
  }
}

